import { AgGridTable } from 'components/AgGrid/AgGridTable';
import type { TextFieldPropValue } from 'components/FieldNumberFormat';
import { RequestStatus } from 'features/request/requestSlice';
import type { WorkOrderPriceRatioRowData } from 'gql/graphql';

import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import type { RefObject } from 'react';
import { useCallback, useState } from 'react';

import { GridRatioUerRowItems } from '../GridRatioUerRowItems/GridRatioUerRowItems';
import type { IVariablesChangeData, IVariablesData } from '../WorkOrderUseCoefficient';

import { useGridPickCoefficientsColumns } from './hooks/useGridPickCoefficientsColumns';
import { useGridPickCoefficientsData } from './hooks/useGridPickCoefficientsData';

export const GridPickCoefficients = ({
    isWorkOrderChange,
    variablesData,
    setCheckedIds,
    checkedIds,
    gridRef,
    variablesChangeData,
    handleChangesValueNote,
    valueNote,
}: {
    variablesData?: IVariablesData;
    isWorkOrderChange?: boolean;
    checkedIds: Record<string, boolean>;
    gridRef: RefObject<AgGridReact<any>>;
    variablesChangeData: IVariablesChangeData;
    setCheckedIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    handleChangesValueNote: (rowId: number) => ({ value }: { value: TextFieldPropValue }) => void;
    valueNote: Record<string, string>;
}) => {
    const [rowPriceRatioData, setRatioRowData] = useState<WorkOrderPriceRatioRowData | undefined>(undefined);

    const displayRatioInfoHandler = useCallback(
        (data) => () => {
            setRatioRowData(data);
        },
        [],
    );

    const closeRatioInfoTable = useCallback(() => {
        setRatioRowData(undefined);
    }, []);

    const { columnDefs } = useGridPickCoefficientsColumns({
        displayRatioInfoHandler,
        setCheckedIds,
        checkedIds,
        handleChangesValueNote,
        valueNote,
        isWorkOrderChange,
    });

    const { datasource, fetchingStatus } = useGridPickCoefficientsData(
        variablesChangeData,
        variablesData,
        isWorkOrderChange,
    );

    const gridOptions = {
        headerHeight: 40,
    };

    return (
        <div className="ag-theme-alpine GridPickCoefficients">
            <AgGridTable
                loading={fetchingStatus === RequestStatus.FETCHING}
                gridOptions={gridOptions}
                datasource={datasource}
                columnDefs={columnDefs}
                containerStyle={{ height: 368 }}
                overlayNoRowsTemplate="Нет данных"
                rowModelType="infinite"
                maxConcurrentDatasourceRequests={1}
                maxBlocksInCache={1000}
                cacheBlockSize={1000}
                ref={gridRef}
            />
            {rowPriceRatioData && (
                <GridRatioUerRowItems
                    variablesData={variablesData}
                    rowPriceRatioData={rowPriceRatioData}
                    closeRatioInfoTable={closeRatioInfoTable}
                    isWorkOrderChange={isWorkOrderChange}
                    variablesChangeData={variablesChangeData}
                />
            )}
        </div>
    );
};

GridPickCoefficients.displayName = 'GridPickCoefficients';
